import { ImageIcon, Sparkles, Upload, Wand2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/shadcn/index.js';
import { Button, FormField, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

const STYLES: Array<{ key: string; label: string; text: string }> = [
  { key: 'studio', label: 'Studio', text: 'studio product shot, seamless white background, soft-box lighting, sharp focus, high detail, commercial photography, 4k' },
  { key: 'ecommerce', label: 'E-commerce', text: 'pure white background, centered, even lighting, no harsh shadows, clean catalog photo' },
  { key: 'lifestyle', label: 'Lifestyle', text: 'lifestyle scene, natural daylight, shallow depth of field, cozy realistic environment, editorial style' },
  { key: 'dark', label: 'Dark & moody', text: 'dark moody background, dramatic rim lighting, premium luxury feel, reflective surface' },
];

interface GeneratedImage { url: string; attachmentId: string | null; downloadUrl: string | null }
interface GenerateResult { id: string; status: 'succeeded' | 'failed'; images: GeneratedImage[]; error: string | null }

function defaultPrompt(name: string, brand?: string, category?: string): string {
  const head = [name, brand, category].filter(Boolean).join(', ');
  return head ? `A high-quality product photo of ${head}` : 'A high-quality product photo';
}

/**
 * AI product image — generate (text→image) or enhance (image→image) via the
 * Nano Banana imagegen module, then import the chosen result into the item
 * gallery. The enhance source can be an uploaded local file (POSTed to
 * /imagegen/source-upload, which returns a public URL the provider can fetch)
 * or a pasted public URL. The result is fetched via its OWN same-origin authed
 * attachment (no cross-origin / SSRF), and added as a normal item image blob.
 */
export function AiImageDialog({
  open, onClose, name, brand, category, onUse,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  brand?: string;
  category?: string;
  onUse: (blob: Blob) => void;
}): JSX.Element {
  const { t } = useTranslation('items');
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<'generate' | 'enhance'>('generate');
  const [source, setSource] = useState('');
  const [style, setStyle] = useState(STYLES[0]!.text);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<GeneratedImage[]>([]);
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPrompt(defaultPrompt(name, brand, category));
    setResults([]);
    setMode('generate');
    setSource('');
  }, [open, name, brand, category]);

  // Upload a local image so the provider (which fetches the source server-side)
  // gets a public URL. The bytes go up raw; the returned URL becomes the source.
  const uploadSource = async (file: File): Promise<void> => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error(t('ai.imgUploadType', { defaultValue: 'Use a PNG, JPEG, WebP or GIF image.' }));
      return;
    }
    setUploading(true);
    try {
      const body = await file.arrayBuffer();
      const r = await api().post<{ url: string }>('/imagegen/source-upload', body, {
        params: { mimeType: file.type },
        headers: { 'Content-Type': 'application/octet-stream' },
        transformRequest: [(d) => d],
      });
      setSource(r.data.url);
    } catch {
      toast.error(t('ai.imgUploadFailed', { defaultValue: 'Upload failed — try a smaller image (max 10 MB).' }));
    } finally {
      setUploading(false);
    }
  };

  const generate = async (): Promise<void> => {
    if (!prompt.trim()) return;
    if (mode === 'enhance' && !source.trim()) { toast.error(t('ai.needSource', { defaultValue: 'Paste a public image URL to enhance.' })); return; }
    setBusy(true);
    setResults([]);
    try {
      const r = await api().post<GenerateResult>('/imagegen/generate', {
        prompt: `${prompt.trim()}. ${style}`,
        type: mode === 'enhance' ? 'IMAGETOIAMGE' : 'TEXTTOIAMGE',
        numImages: 1,
        ...(mode === 'enhance' && source.trim() ? { imageUrls: [source.trim()] } : {}),
      });
      if (r.data.status === 'succeeded' && r.data.images.length > 0) setResults(r.data.images);
      else toast.error(r.data.error ?? t('ai.imgFailed', { defaultValue: 'Generation failed. Try again.' }));
    } catch {
      toast.error(t('ai.imgFailed', { defaultValue: 'Could not generate — check Settings → AI Images.' }));
    } finally {
      setBusy(false);
    }
  };

  const use = async (img: GeneratedImage): Promise<void> => {
    if (!img.attachmentId) { toast.error(t('ai.imgImportFailed', { defaultValue: 'Could not import that image.' })); return; }
    setImporting(img.attachmentId);
    try {
      const res = await api().get(`/attachments/${img.attachmentId}/download`, { responseType: 'blob' });
      onUse(res.data as Blob);
      onClose();
    } catch {
      toast.error(t('ai.imgImportFailed', { defaultValue: 'Could not import that image.' }));
    } finally {
      setImporting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand" aria-hidden />{t('ai.imgTitle', { defaultValue: 'AI product image' })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            {(['generate', 'enhance'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${mode === m ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
              >
                {m === 'generate' ? t('ai.imgGenerate', { defaultValue: 'Generate' }) : t('ai.imgEnhance', { defaultValue: 'Enhance a photo' })}
              </button>
            ))}
          </div>

          {mode === 'enhance' && (
            <FormField id="ai-img-src" label={t('ai.imgSource', { defaultValue: 'Source image' })} helperText={t('ai.imgSourceHint', { defaultValue: 'Upload a photo from your device, or paste a publicly reachable image URL.' })}>
              <div className="flex flex-col gap-2">
                <div className="flex items-stretch gap-2">
                  <Input id="ai-img-src" value={source} onChange={(e) => setSource(e.target.value)} placeholder="https://…" className="flex-1" />
                  <label className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 text-sm text-fg-muted transition hover:text-fg ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
                    <Upload className="h-4 w-4" aria-hidden />
                    {uploading ? t('ai.imgUploading', { defaultValue: 'Uploading…' }) : t('ai.imgUpload', { defaultValue: 'Upload' })}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadSource(f); e.target.value = ''; }}
                    />
                  </label>
                </div>
                {source.trim() && (
                  <img
                    src={source}
                    alt=""
                    className="h-24 w-24 rounded-md border border-border object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.display = ''; }}
                  />
                )}
              </div>
            </FormField>
          )}

          <FormField id="ai-img-prompt" label={t('ai.imgPrompt', { defaultValue: 'Prompt' })}>
            <textarea
              id="ai-img-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
          </FormField>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-fg-muted">{t('ai.imgStyle', { defaultValue: 'Style' })}</span>
            {STYLES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStyle(s.text)}
                aria-pressed={style === s.text}
                className={`rounded-md border px-2.5 py-1 text-xs ${style === s.text ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <Button type="button" onClick={() => void generate()} loading={busy} disabled={!prompt.trim() || uploading}>
            <Wand2 className="h-4 w-4" aria-hidden />
            {busy ? t('ai.imgGenerating', { defaultValue: 'Generating… (up to a minute)' }) : t('ai.imgGo', { defaultValue: 'Generate image' })}
          </Button>

          {results.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {results.map((img) => (
                <div key={img.url} className="overflow-hidden rounded-lg border border-border">
                  <img src={img.url} alt="" className="aspect-square w-full object-cover" />
                  <div className="grid grid-cols-2">
                    <Button type="button" variant="secondary" size="sm" className="rounded-none border-e" loading={importing === img.attachmentId} onClick={() => void use(img)}>
                      <ImageIcon className="h-4 w-4" aria-hidden />
                      {t('ai.imgUse', { defaultValue: 'Use this' })}
                    </Button>
                    {/* Iterate: refine this result (its public URL feeds image→image). */}
                    <Button type="button" variant="ghost" size="sm" className="rounded-none" onClick={() => { setMode('enhance'); setSource(img.url); setResults([]); }}>
                      <Wand2 className="h-4 w-4" aria-hidden />
                      {t('ai.imgRefine', { defaultValue: 'Refine' })}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
